import type { Active, UniqueIdentifier } from '@dnd-kit/core'
import { DndContext, KeyboardSensor, MouseSensor, useSensor, useSensors } from '@dnd-kit/core'
import React, { useMemo, useState } from 'react'
import { SortableContext, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import SortableItem, { DragHandle } from './SortableItem'

import type { ReactNode } from 'react'
import SortableOverlay from './SortableOverlay'
import styled from 'styled-components'

export type BaseItem = {
  id: UniqueIdentifier
}

interface Props<T extends BaseItem> {
  items: T[]
  onChange(items: T[]): void
  renderItem(item: T, index: number): ReactNode
  className?: string
}

export default function SortableList<T extends BaseItem>({
  items,
  onChange,
  renderItem,
  className,
}: Props<T>) {
  const [active, setActive] = useState<Active | null>(null)

  const activeIndex = useMemo(() => {
    const activeIndex = items?.findIndex((item) => active && item.id === active?.id)
    return activeIndex
  }, [active, items])

  const activeItem = useMemo(() => {
    return items[activeIndex]
  }, [activeIndex, items])

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => {
        setActive(active)
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id)
          const overIndex = items.findIndex(({ id }) => id === over.id)

          onChange(arrayMove(items, activeIndex, overIndex))
        }
        setActive(null)
      }}
      onDragCancel={() => {
        setActive(null)
      }}
    >
      <SortableContext items={items}>
        <SortableListStyled role="application" className={className}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
          ))}
        </SortableListStyled>
      </SortableContext>

      <SortableOverlay>
        {activeItem && activeIndex ? renderItem(activeItem, activeIndex) : null}
      </SortableOverlay>
    </DndContext>
  )
}

SortableList.Item = SortableItem
SortableList.DragHandle = DragHandle

const SortableListStyled = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0;
  list-style: none;
`



// WEBPACK FOOTER //
// ./src/components/SortableList/index.tsx